import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { AudioPlayerService } from 'src/app/core/services/audio-player.service';
import { CurriculumService } from 'src/app/core/services/curriculum.service';
import { InterventionTaskService } from 'src/app/core/services/intervention-task.service';
import { ApplicationStateService } from '../../core/services/application-state.service';
import { SessionTimerService } from 'src/app/core/services/session-timer.service';
import { StudentDataService } from 'src/app/core/services/student-data.service';
import { ThemeParseService } from 'src/app/core/services/theme-parse.service';
import { LoginService } from '../login.service';
import { SSOError, Credentials, AlertDetails } from '../../core/models/credentials.model';
import { ClasslinkService } from './classlink.service' ;
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import * as Sentry from '@sentry/angular';
import { BackgroundComponent } from '../../shared/components/background/background.component';
import { VersionFooterComponent } from '../../shared/components/version-footer/version-footer.component';

@Component({
    selector: 'wf-classlink',
    templateUrl: './classlink.component.html',
    styleUrls: ['./classlink.component.css'],
    imports: [BackgroundComponent, VersionFooterComponent]
})
export class ClasslinkComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private audioPlayerService = inject(AudioPlayerService);
  private classlinkService = inject(ClasslinkService);
  private curriculumService = inject(CurriculumService);
  private interventionTaskService = inject(InterventionTaskService);
  private loginService = inject(LoginService);
  private applicationStateService = inject(ApplicationStateService);
  private sessionTimerService = inject(SessionTimerService);
  private studentDataService = inject(StudentDataService);
  private themeParseService = inject(ThemeParseService);

  private subscriptions: Subscription = new Subscription();
  private reusableTimeout?: number;

  token: string = "";
  appStoreLink: string = '' ;
  invalidToken: boolean = false;
  loadingToken: boolean = false;
  showWelcome: boolean = false;
  showUsage: boolean = false;
  restrictLogin: boolean = true ;
  error?: SSOError;
  alert?: AlertDetails;

  // Lifecycle Hooks
  ngOnInit(): void {
    this.token = this.activatedRoute.snapshot.queryParams["token"];
    this.loadingToken = true;
    if (this.invalidToken)
    {
      this.error = {
        message: 'Could not communicate with Classlink',
        details: 'We could not communicate with Classlink to get a valid login and retrieve your user info',
        showRetry: true,
      }
      this.loadingToken = false;

      return;
    }

    this.appStoreLink = environment.appStoreLink ;

    this.getUserInfo();
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.reusableTimeout);
    this.subscriptions.unsubscribe();
  }
  // End of lifecycle hooks

  private onLoginSuccess() {
    this.interventionTaskService.resetData();
    this.studentDataService.loadAvailableAvatars();
    const studentCompletionResult = this.loginService.studentCompletionStatus(this.studentDataService.getStudentData());

    if (this.studentDataService.isDemoUser())
    {
      // Have the demo user select the curriculum
      this.goToDemoSelectScreen();
    }
    else if (studentCompletionResult.status === 'complete')
    {
      // All tasks are complete so don't allow student to use again
      this.applicationStateService.clear();
      this.alert = {
        'message': 'All Done!',
        'details' : studentCompletionResult.message,
      } ;

      // Logout the user after showing the already completed text for a minute
      window.clearTimeout(this.reusableTimeout);
      this.reusableTimeout = window.setTimeout(() => {
        this.sessionTimerService.cancelTimers();
        this.router.navigateByUrl('/login')
      }, 60000);
    }
    else if (this.studentDataService.getStudentData()?.overWeeklyUsageLimit)
    {
      // Initiates the pop-out animation for the login box and
      // waits 1.5 seconds before showing the excessive usage dialog
      setTimeout(() => {
        this.showUsage = true;
        this.sessionTimerService.cancelTimers();
      }, 1500);
    }
    else
    {
      // Load curriculum
      let curriculumSubscription = this.curriculumService.loadCurriculum().subscribe(() => {
        // If we are logging in via SSO, we need to have the user interact with the site
        // otherwise any media played will be considered 'auto-played' and be blocked in the browser -- so display our
        // simple welcome dialog that requires the user to click the button to continue
        this.showWelcome = true ;
        document.getElementById("login-wait")?.classList.add('pop-out', 'hidden');
      });

      this.subscriptions.add(curriculumSubscription);
    }
  }

  private onLoginError(err?: any) {
    let errorCode = err?.status || -1;
    const errorMsg = err?.error?.message || '' ;
    if (errorCode === 426)
    {
      this.error = {
        'message' : 'Login failed',
        'details' : errorMsg || 'This version of the app is no longer supported, please update to continue using WordFlight',
        'showRetry' : false,
      } ;
    }
    else
    {
      this.error = {
        'message' : 'Login failed',
        'details' : 'An error occurred while trying to login to WordFlight',
        'showRetry' : true,
      } ;
    }
    this.loadingToken = false;

    if (err)
    {
      Sentry.captureException(err, {
        tags: {
          section: 'login',
          from: 'clever',
        }
      })
    }
  }

  private login(credentials: Credentials) {
    let loginSubscription = this.loginService.loginWithChecks(credentials.wf.username, '', credentials.sso).subscribe({
      next: (response) => {
        switch (response.status) {
          case 'maintenance':
            this.error = {
              message : 'Scheduled Maintenance',
              details: 'We are currently working on the application. Please try again later.',
              showRetry: false,
            } ;

            break ;

          case 'update':
            this.error = {
              message : 'A new version is available',
              details: 'A new version is available. The application will reload shortly...',
              showRetry: false,
            } ;

            setTimeout(() => {
              window.location.reload();
            }, 1500);

            break ;

          case 'success':
            const { loginResponse } = response ;

            if (loginResponse.isLoggedIn)
            {
              this.onLoginSuccess();
            }
            else
            {
              this.onLoginError();
            }
            break ;
        }
      },
      error: (err: any) => {
        this.onLoginError(err);
      }
    });

    this.subscriptions.add(loginSubscription);
  }

  private goToDemoSelectScreen() {
    this.router.navigateByUrl('/demo');
  }

  private goToStartScreen() {
    let themeSubscription = this.themeParseService.loadThemeProperties().subscribe(() => {
      // TODO: Get rid of document and run animations in a more 'Angular' way
      let audioSubscription = this.audioPlayerService.play('Audio/Other/login.mp3').subscribe();
      this.subscriptions.add(audioSubscription);
      document.getElementById("background-overlay")?.classList.add('fade-out');
      document.getElementById("fil-logo")?.classList.add('fade-out');
      document.getElementById("version")?.classList.add('fade-out');

      window.clearTimeout(this.reusableTimeout);
      this.reusableTimeout = window.setTimeout(() => {
        this.studentDataService.setSideMenuOpen(true);

        if (this.studentDataService.isFullProductSubscription())
        {
          this.router.navigateByUrl('/world');
        }
        else
        {
          this.router.navigateByUrl('/levelSelect', { state: { previous: this.router.url } });
        }
      }, 2000)
    });

    this.subscriptions.add(themeSubscription);
  }

  getUserInfo() {
    // Before we even attempt to get our user info and login, determine if we are on iOS in a browser window,
    // if so abort any login and display our App message
    /**
     * if determine this is a mac/ios - cannot rely on Safari b/c chrome os reports that
     * if determine this is mobile - multitouchpoints
     * if determine this is web - capacitor getplatform === 'web'
     * then do not show web login
     */
    this.restrictLogin = Capacitor.getPlatform() === 'web' && (window.navigator.maxTouchPoints > 1) && (window.navigator.userAgent.toLowerCase().indexOf('mac') > -1) ;
    if (this.restrictLogin)
    {
      // No longer loading, but we do not want to continue login process so return
      this.loadingToken = false ;

      return ;
    }

    let userInfoSubscription = this.classlinkService.getUserInfo(this.token).pipe(
      switchMap((userInfoObj: any) => {
        if (userInfoObj.wf.username) {
          return of({ userObj : userInfoObj }) ;
        }
        else
        {
          // If we made it here and we are authorized via District SecureSync, our Clever login was valid but
          // we do not have a WF account for the student, set our SSO info and attempt to communicate with Clever and get teacher info
          this.applicationStateService.setSSOInfo(userInfoObj.sso) ;
          return this.classlinkService.rosterStudent(userInfoObj.sso) ;
        }
      })
    ).subscribe({
      next: (value : { userObj?: any, error?: SSOError }) => {
        this.loadingToken = false;

        if (value.userObj.wf.username)
        {
          this.sessionTimerService.cancelTimers();
          return this.login(value.userObj);
        }
        else
        {
          this.studentDataService.setIsLoggedIn(false);
          this.error = {
            message: 'WordFlight Account Not Found',
            details: 'It doesn\'t appear that your WordFlight account has been linked to your Classlink account',
            showRetry: true,
          }
        }
      },
      error: (error) => {
        this.studentDataService.setIsLoggedIn(false);
        if (error.error && error.error.message)
        {
          this.error = error.error ;
        }
        else
        {
          this.error = {
            message: 'Cannot reach Classlink',
            details: 'It looks like we are having trouble communicating with Classlink. Check your internet connection and try again.',
            showRetry: true,
          };

          // Other error cases would be handled and reported correctly, this catch all needs to go to Sentry
          Sentry.captureException(error, {
            tags: {
              section: 'classlink-login',
            }
          }) ;
        }
        this.loadingToken = false;
      }
    });

    this.subscriptions.add(userInfoSubscription);
  }

  acceptWelcome() {
    document.getElementById('welcome')?.classList.add('pop-out');
    this.goToStartScreen();
  }

  cancelLogin() {
    this.showUsage = false;

    this.loginService.studentUsageAlert().subscribe();

    window.clearTimeout(this.reusableTimeout);
    this.reusableTimeout = window.setTimeout(() => {
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.onSameUrlNavigation = 'reload';
      this.router.navigate(['/login']);
    }, 1000);
  }
}
